/* ──────────────────────────────────────────────────────────────────────────────
   Faz 4 — Satıcıya İade: iade ekranlarının PAYLAŞTIĞI katman (invoiceShared.js deseninde).

   Dosyanın GÖVDESİ saf yardımcılardır (sabit / biçimlendirme / iş kuralı fonksiyonları).
   En SONDAKİ "c360 LİSTE KATMANI" bölümü, /vendor-returns ve /gib-monitor ekranlarının
   c360 liste motoruna (useC360List) bağlanırken PAYLAŞTIĞI parçaları taşır: OData $expand
   sabiti, satır normalizasyonu, kolon-filtre meta'sı, lookup arayıcıları ve bozuk-iade
   denetim hook'u. Bunlar İKİ EKRANDA DA aynıdır; birinde kopyalamak, denetim kuralının
   (spec §10) iki yerde ayrışması demekti.

   Sunucu karşılıkları — DEĞERLER KODDAN ALINDI, uydurulmadı:
     • Status .............. DataScope.Entities.Static.ReturnStatuses
     • ReturnType .......... ReturnTypes.VehicleToDealer
     • ReturnSubType ....... ReturnSubTypes (ROBUST | SCRAP)
     • DamageReason ........ ReturnDamageReasons
     • EDocumentType ....... EDocumentTypes (1=e-Fatura, 2=e-Arşiv)
     • StockType ........... Enums.StockType (0=Satılabilir … 3=Hasarlı)

   ══ EKRANLARIN OKUMASI GEREKEN İKİ GERÇEK ═══════════════════════════════════
   1) GİB REDDİ STOĞU GERİ ALMAZ. Reddedilen belge RESMÎ DEĞİLDİR ama mal ARAÇTAN
      ÇIKMIŞTIR (iş kuralı 9). Bu ikilik ekranda kırmızıyla söylenmezse kimse
      müdahale etmez → `stockAlreadyDeducted` / RED bandı bu yüzden var.
   2) HURDA SUİSTİMALE AÇIK BİR KALEMDİR (spec §10). Fotoğrafsız veya nedeni
      "diğer" olan bozuk iade, denetlenmesi gereken iadedir → `isSuspiciousScrap`.
   ────────────────────────────────────────────────────────────────────────────── */
import { useCallback, useEffect, useRef, useState } from 'react';
import { s, authHeaders } from './listPageStyles';

export const API_BASE = '/api/returns';
export const VENDORS_API = '/api/vendors';
export const REPORTS_API = '/api/reports';

/** Satıcıya iade — ReturnHeader.ReturnType. Müşteri iadeleri (CUSTOMER_*) bu ekranlara GİRMEZ. */
export const RETURN_TYPE_VENDOR = 'VEHICLE_TO_DEALER';

// ═══════════════════════════════════════════════════════════ DURUM (ReturnStatuses)

export const RETURN_STATUS = {
  TASLAK: 0,
  GONDERILDI: 1,
  ISLENDI: 2,
  GIB_KABUL: 3,
  GIB_RED: 4,
  IPTAL: 5,
};

export const RETURN_STATUS_OPTIONS = [
  { value: 0, label: 'Taslak', tone: 'neutral' },
  { value: 1, label: 'Gönderildi', tone: 'wait' },
  { value: 2, label: 'İşlendi', tone: 'wait' },
  { value: 3, label: 'GİB Kabul', tone: 'ok' },
  { value: 4, label: 'GİB Red', tone: 'bad' },
  { value: 5, label: 'İptal', tone: 'neutral' },
];

export const statusLabel = (v) => {
  const found = RETURN_STATUS_OPTIONS.find((o) => o.value === Number(v));
  return found ? found.label : '-';
};
export const statusTone = (v) => {
  const found = RETURN_STATUS_OPTIONS.find((o) => o.value === Number(v));
  return found ? found.tone : 'neutral';
};

/** Belge KESİLMİŞ mi (stok düşmüş mü) — sunucudaki ReturnStatuses.IsPosted ile birebir. */
export const isPosted = (v) => [1, 2, 3, 4].includes(Number(v));

/** Yalnız Taslak ve Gönderildi iptal edilebilir (spec §4 / ReturnStatuses.IsCancellable). */
export const isCancellable = (v) => [0, 1].includes(Number(v));

// ═══════════════════════════════════════════════════════════ GİB DURUMU
// GİB durumu Status'ün TÜRETİLMİŞ görünümüdür (ayrı kolon yok): belge kesildiği an
// GİB kuyruğuna girer; kabul/red oradan döner.

export const GIB_STATUS = {
  0: { label: '—', tone: 'neutral' },
  1: { label: "GİB'e iletildi", tone: 'wait' },
  2: { label: 'İşlendi', tone: 'wait' },
  3: { label: 'Kabul', tone: 'ok' },
  4: { label: 'Reddedildi', tone: 'bad' },
  5: { label: 'İptal', tone: 'neutral' },
};
export const gibLabel = (v) => GIB_STATUS[Number(v)]?.label || '—';
export const gibTone = (v) => GIB_STATUS[Number(v)]?.tone || 'neutral';

/** GİB izleme filtresi (backend: GET /api/returns/gib-status?status=rejected|pending). */
export const GIB_FILTER_OPTIONS = [
  { value: '', label: 'GİB: Tümü' },
  { value: '4', label: 'Reddedildi' },
  { value: '1', label: "GİB'e iletildi" },
  { value: '3', label: 'Kabul' },
  { value: '0', label: 'Taslak' },
  { value: '5', label: 'İptal' },
];

// ═══════════════════════════════════════════════════════════ ALT TİP (ReturnSubTypes)
// BİR BELGEDE TEK ALT TİP (iş kuralı 1): sağlam ve bozuk farklı stok havuzundan düşer,
// satıcının deposunda farklı sürece girer, bozuk olan KANIT ister.

export const SUB_TYPE = {
  ROBUST: { value: 'ROBUST', label: 'Sağlam', tone: 'ok', stockType: 0, hint: 'Yeniden satılabilir · satılabilir stoktan düşer' },
  SCRAP: { value: 'SCRAP', label: 'Bozuk', tone: 'wait', stockType: 3, hint: 'Hurda / imha · hasarlı stoktan düşer · fotoğraf + neden zorunlu' },
};

export const SUB_TYPE_OPTIONS = [
  { value: 'ROBUST', label: 'Sağlam' },
  { value: 'SCRAP', label: 'Bozuk' },
];

export const normalizeSubType = (v) => String(v || '').trim().toUpperCase();
export const isScrap = (v) => normalizeSubType(v) === 'SCRAP';
export const subTypeLabel = (v) => SUB_TYPE[normalizeSubType(v)]?.label || '-';
export const subTypeTone = (v) => SUB_TYPE[normalizeSubType(v)]?.tone || 'neutral';

// ═══════════════════════════════════════════════════════════ HASAR NEDENİ

export const DAMAGE_REASON_OPTIONS = [
  { value: 'kirik_ambalaj', label: 'Kırık ambalaj' },
  { value: 'skt_gecmis', label: 'SKT geçmiş' },
  { value: 'sogutma_zinciri', label: 'Soğutma zinciri' },
  { value: 'tasima_hasari', label: 'Taşıma hasarı' },
  { value: 'diger', label: 'Diğer' },
];

export const damageReasonLabel = (v) => {
  const key = String(v || '').trim().toLowerCase();
  return DAMAGE_REASON_OPTIONS.find((o) => o.value === key)?.label || (v || '-');
};

/** Gerekçesi "diğer" olan hurda, gerekçesi olmayan hurdadır — denetim listesine düşer. */
export const isVagueDamageReason = (v) => {
  const key = String(v || '').trim().toLowerCase();
  return !key || key === 'diger';
};

// ═══════════════════════════════════════════════════════════ e-BELGE / STOK TİPİ

export const E_DOCUMENT_TYPE = { 1: 'e-Fatura', 2: 'e-Arşiv' };
export const eDocumentLabel = (v) => E_DOCUMENT_TYPE[Number(v)] || '—';

export const STOCK_TYPE_LABELS = { 0: 'Satılabilir', 1: 'Bedelsiz', 2: 'Depozito', 3: 'Hasarlı' };
export const stockTypeLabel = (v) => STOCK_TYPE_LABELS[Number(v)] ?? '-';

// ═══════════════════════════════════════════════════════════ BOZUK İADE DENETİMİ (spec §10)

/**
 * Şüpheli hurda: fotoğrafsız VEYA nedeni "diğer". Sunucu bunları KABUL ETMEZ (iş kuralı 5) —
 * yine de listelenirler: eski kayıtlar, elle veri düzeltmeleri ve kural devreye girmeden önce
 * kesilmiş belgeler buradan görünür. Denetimin işi kuralın tuttuğunu VARSAYMAK değil, DOĞRULAMAK.
 *
 * @param {{subType:string, lines?:Array, photoCount?:number, damageReasons?:string[]}} doc
 */
export function isSuspiciousScrap(doc) {
  if (!doc || !isScrap(doc.subType)) return false;
  const noPhoto = Number(doc.photoCount ?? 0) === 0;
  const reasons = doc.damageReasons || [];
  const vague = reasons.length === 0 || reasons.some(isVagueDamageReason);
  return noPhoto || vague;
}

/** GetById yanıtındaki `items` → denetim özeti (foto sayısı + hasar nedenleri). */
export function auditSummaryFromItems(items) {
  const lines = Array.isArray(items) ? items : [];
  return {
    photoCount: lines.reduce((n, l) => n + (l.photos?.length || 0), 0),
    damageReasons: lines.map((l) => l.damageReason).filter((r) => r != null),
    lineCount: lines.length,
    totalQuantity: lines.reduce((n, l) => n + Number(l.quantity || 0), 0),
  };
}

// ═══════════════════════════════════════════════════════════ DAĞITIM DOĞRULAMASI

/** Σ allocation = satır miktarı (iş kuralı 2). Tutmayan satır belgede KIRMIZI gösterilir. */
export function allocationDiff(line) {
  const qty = Number(line?.quantity || 0);
  const allocated = (line?.allocations || []).reduce((n, a) => n + Number(a.quantity || 0), 0);
  return { qty, allocated, diff: Number((qty - allocated).toFixed(4)) };
}

/** Satırın dayandığı alış faturası numaraları (GİB'in belgede görmek istediği bilgi). */
export function invoiceNosOf(lines) {
  const seen = new Set();
  (lines || []).forEach((l) => (l.allocations || []).forEach((a) => {
    if (a.purchaseInvoiceNo) seen.add(a.purchaseInvoiceNo);
  }));
  return [...seen];
}

// ═══════════════════════════════════════════════════════════ BİÇİMLENDİRME

export const DATE_PRESETS = [
  { value: '7', label: 'Son 7 gün' },
  { value: '30', label: 'Son 30 gün' },
  { value: '90', label: 'Son 90 gün' },
  { value: '', label: 'Tümü' },
];

export function fmtDate(v) {
  if (!v) return '-';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '-' : d.toLocaleDateString('tr-TR', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function fmtDateTime(v) {
  if (!v) return '-';
  const d = new Date(v);
  return Number.isNaN(d.getTime())
    ? '-'
    : d.toLocaleString('tr-TR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

export function fmtQty(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n.toLocaleString('tr-TR', { maximumFractionDigits: 2 }) : '-';
}

const moneyFormatter = new Intl.NumberFormat('tr-TR', {
  style: 'currency', currency: 'TRY', minimumFractionDigits: 2, maximumFractionDigits: 2,
});
export function fmtMoney(v) {
  const n = Number(v);
  return Number.isFinite(n) ? moneyFormatter.format(n) : '-';
}

/** "42 dk" / "6s 20dk" / "3 gün" — gönderim bekleyenlerin süre kolonu. */
export function fmtWaiting(hours) {
  const h = Number(hours);
  if (!Number.isFinite(h) || h < 0) return '-';
  if (h < 1) return '< 1 sa';
  if (h < 24) return `${Math.floor(h)} sa`;
  return `${Math.floor(h / 24)} gün`;
}

/** OData/ApiResponse zarflarını soyar. */
export function unwrap(body) {
  return body?.data ?? body?.Data ?? body;
}
export function odataRows(body) {
  return body?.value || body?.Value || (Array.isArray(body) ? body : []);
}

// ═══════════════════════════════════════════════════════════════════════════════════════════════
// c360 LİSTE KATMANI — /vendor-returns ve /gib-monitor'ün ORTAK parçaları
// ═══════════════════════════════════════════════════════════════════════════════════════════════

/** Ortak stiller (karar blokları + uyarı bandı). ReturnableQuantitiesReportPage da kullanır. */
export const rst = {
  blk: { borderTop: '1px solid var(--border-color, rgba(15,23,42,0.10))', padding: '14px 16px' },
  blkDanger: { background: 'var(--danger-soft, rgba(226, 68, 92,0.07))' },
  blkTitle: {
    margin: '0 0 10px', fontSize: 11, fontWeight: 800, letterSpacing: 0.4, textTransform: 'uppercase',
    color: 'var(--text-secondary, #6b7280)', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
  },
  blkTitleDanger: { color: 'var(--danger, #e2445c)' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: {
    textAlign: 'left', padding: '8px 9px', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap',
    color: 'var(--text-secondary, #6b7280)', borderBottom: '1px solid var(--border-color, rgba(15,23,42,0.10))',
  },
  td: {
    padding: '9px', whiteSpace: 'nowrap', color: 'var(--text-primary, #111827)',
    borderBottom: '1px solid var(--border-color, rgba(15,23,42,0.06))',
  },
  num: { textAlign: 'right' },
  mono: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontWeight: 700 },
  empty: { padding: '14px 4px', fontSize: 13, color: 'var(--text-secondary, #6b7280)' },
  smallBtn: { ...s.btnSecondary, padding: '5px 9px', fontSize: 11, minWidth: 0, height: 'auto' },
  chip: {
    padding: '5px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700, cursor: 'pointer',
    border: '1px solid var(--border-color, rgba(15,23,42,0.14))', background: 'var(--bg-card, #fff)',
    color: 'var(--text-secondary, #6b7280)', userSelect: 'none',
  },
  chipOn: { background: 'var(--accent, #0073ea)', color: '#fff', borderColor: 'transparent' },
  chipDanger: { background: 'var(--danger, #e2445c)', color: '#fff', borderColor: 'transparent' },
  filters: { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' },
  filterInput: { ...s.input, height: 34, fontSize: 12, borderRadius: 6, width: 140 },
  filterSelect: { ...s.select, height: 34, fontSize: 12, borderRadius: 6, width: 160 },
  danger: { color: 'var(--danger, #e2445c)', fontWeight: 800 },
  muted: { color: 'var(--text-secondary, #6b7280)' },
  band: {
    display: 'flex', gap: 10, alignItems: 'flex-start', padding: '12px 14px',
    background: 'var(--danger-soft, rgba(226, 68, 92,0.09))',
    border: '1px solid var(--danger, #e2445c)', borderRadius: 10, margin: '0 0 12px',
  },
};

/**
 * Liste $expand'ı. Kalem / Miktar / "İlişkili Alış Fat." kolonları alt koleksiyonlardan gelir;
 * satır başına ikinci istek atmak (N+1) listeyi çökertirdi → TEK ATIMDA inerler.
 * DİKKAT: $expand veren liste $select VERMEZ (sunucu expand'lı nav'ı $select dışında düşürür).
 */
export const RETURN_LIST_EXPAND = 'ReturnDetails($expand=Allocations)';

/** Her zaman uygulanan taban filtre: bu ekranlar YALNIZ satıcıya iadeyi gösterir. */
export const VENDOR_RETURN_BASE_FILTER = `ReturnType eq '${RETURN_TYPE_VENDOR}'`;

/** Tarih ön-ayarı → OData cümlesi ('' = Tümü). */
export function datePresetClause(datePreset) {
  if (!datePreset) return '';
  const since = new Date(Date.now() - Number(datePreset) * 86400000);
  return `DocumentDate ge ${since.toISOString()}`;
}

/** Taban filtre + tarih ön-ayarı (+ opsiyonel ek cümleler) → tek $filter. */
export function buildVendorReturnBaseFilter(datePreset, ...extra) {
  return [VENDOR_RETURN_BASE_FILTER, datePresetClause(datePreset), ...extra]
    .filter(Boolean)
    .map((c) => `(${c})`)
    .join(' and ');
}

/**
 * OData satırını (ReturnHeader + $expand) ekranın okuduğu düz şekle çevirir.
 * Kalem sayısı / toplam miktar / dayanılan alış faturaları BURADA türetilir — iki ekran da aynı
 * türetmeyi kullanır, aksi halde aynı belge iki listede farklı "Kalem" gösterebilirdi.
 */
export function mapReturnRow(r) {
  const lines = r.returnDetails || r.ReturnDetails || [];
  return {
    ...r,
    lines,
    lineCount: lines.length,
    totalQuantity: lines.reduce((n, l) => n + Number(l.quantity || 0), 0),
    purchaseInvoiceNos: invoiceNosOf(lines.map((l) => ({ allocations: l.allocations || l.Allocations || [] }))),
    damageReasons: lines.map((l) => l.damageReason).filter((x) => x != null),
    productIds: lines.map((l) => l.productId).filter(Boolean),
  };
}

/**
 * ÜRÜN FİLTRESİ — alt koleksiyon üzerinde lambda. `field eq value` kalıbına sığmadığı için
 * c360'ın `buildClause` kaçış kapısından geçer (filterModel.js). Eskiden ürün filtresi
 * CLIENT-SIDE idi: yalnız inen 500 satırda arardı, 501. belgeyi HİÇ bulamazdı.
 */
export const productAnyClause = (filter) => (
  filter?.value ? `ReturnDetails/any(d: d/ProductId eq ${filter.value})` : null
);

/**
 * `ReturnDetails/any(...)` bu uçta destekleniyor mu? ReturnHeader'ın OData modeli CLR tipinden
 * KONVANSİYONLA üretilir; koleksiyon navigasyonu modele girmemişse any() 400 döner. ($expand
 * çalışıyor olması güçlü bir işarettir ama GARANTİ değildir — depositShared.js aynı soruyu aynı
 * şekilde soruyor.)
 *
 * Desteklenmiyorsa ürün filtresi UYGULANMAZ (satır ELENMEZ) ve huni gösterilmez: 400 dönen bir
 * filtre listeyi komple "Veri alınamadı"ya düşürürdü. Sonuç süreç boyu cache'lenir.
 */
let detailFilterProbe = null;
export function supportsDetailFilter() {
  if (!detailFilterProbe) {
    detailFilterProbe = (async () => {
      try {
        const url = `${API_BASE}?$top=1&$filter=${encodeURIComponent('ReturnDetails/any(d: d/Quantity gt 0)')}`;
        const res = await fetch(url, { headers: authHeaders() });
        return res.ok;
      } catch {
        return false;
      }
    })();
  }
  return detailFilterProbe;
}

/**
 * İki ekranın PAYLAŞTIĞI kolon-filtre meta'sı (sunucu tarafı huniler).
 *
 * ⚠ `status` TEK kolondur: "GİB Durumu" Status'ün TÜRETİLMİŞ görünümüdür, ayrı bir kolon YOKTUR
 * (bkz. GİB DURUMU bölümü). Bu yüzden Durum ve GİB Durumu için İKİ ayrı huni kurulmaz — aynı
 * alana iki cümle VE'lenirse (Status eq 0 and Status eq 4) liste sessizce BOŞ dönerdi.
 */
const BASE_COLUMN_FILTER_META = {
  returnNo: { type: 'text', odataField: 'ReturnNo' },
  documentDate: { type: 'date', odataField: 'DocumentDate' },
  representative: { type: 'select', odataField: 'RepresentativeId', valueFormat: 'guid' },
  vendor: { type: 'select', odataField: 'VendorId', valueFormat: 'guid' },
  returnSubType: { type: 'select', odataField: 'ReturnSubType', valueFormat: 'string', options: SUB_TYPE_OPTIONS },
  status: { type: 'select', odataField: 'Status', options: RETURN_STATUS_OPTIONS },
  gibRetryCount: { type: 'number', odataField: 'GibRetryCount' },
};

const PRODUCT_COLUMN_FILTER_META = {
  ...BASE_COLUMN_FILTER_META,
  product: { type: 'asyncSelect', buildClause: productAnyClause },
};

/**
 * Kolon-filtre meta'sı. `productSupported === false` ise Ürün hunisi DÜŞER (sunucu lambda'yı
 * kabul etmiyor); null/true iken açıktır. Referans STABİLDİR (useC360List sonsuz döngüye girmesin).
 */
export function returnColumnFilterMeta(productSupported) {
  return productSupported === false ? BASE_COLUMN_FILTER_META : PRODUCT_COLUMN_FILTER_META;
}

/** Serbest metin araması — belge no (spec: "searchFields: belge no"). */
export const RETURN_SEARCH_FIELDS = ['ReturnNo'];

// ─────────────────────────────────────────────────────────────────── Lookup'lar

/** Plasiyer + satıcı cari listeleri (KÜÇÜK master'lar — bir kez çekilir, huniyi besler). */
export async function fetchReturnLookups() {
  const headers = authHeaders();
  const grab = async (url) => {
    try {
      const res = await fetch(url, { headers });
      return res.ok ? odataRows(unwrap(await res.json())) : [];
    } catch {
      return [];
    }
  };
  const [representatives, vendors] = await Promise.all([
    grab('/api/representatives/Select'),
    grab('/api/vendors/Select'),
  ]);
  return { representatives, vendors };
}

/** productId → "KOD - Ad" (kolon etiketi + dışa aktarım). */
export async function fetchProductLabels(ids) {
  const need = [...new Set((ids || []).filter(Boolean))].slice(0, 400);
  if (!need.length) return {};
  try {
    const filter = need.map((x) => `Id eq ${x}`).join(' or ');
    const res = await fetch(
      `/api/products?$top=500&light=1&$filter=${encodeURIComponent(`(${filter})`)}`,
      { headers: authHeaders() },
    );
    if (!res.ok) return {};
    const map = {};
    odataRows(await res.json()).forEach((p) => {
      const id = p.id ?? p.Id;
      const code = p.textId ?? p.TextId ?? '';
      const name = p.name ?? p.Name ?? '';
      if (id) map[id] = [code, name].filter(Boolean).join(' - ') || String(id);
    });
    return map;
  } catch {
    return {};
  }
}

/** Ürün huni araması (asyncSelect fetchOptions) — SUNUCU tarafı, tüm katalogda arar. */
export async function searchProductOptions(q) {
  const query = String(q || '').replace(/'/g, "''").toLowerCase();
  let url = '/api/products?$top=30&$orderby=TextId&light=1';
  if (query) {
    url += `&$filter=${encodeURIComponent(`contains(tolower(TextId),'${query}') or contains(tolower(Name),'${query}')`)}`;
  }
  try {
    const res = await fetch(url, { headers: authHeaders() });
    if (!res.ok) return [];
    return odataRows(await res.json()).map((p) => {
      const code = p.textId ?? p.TextId ?? '';
      const name = p.name ?? p.Name ?? '';
      return { value: p.id ?? p.Id, label: [code, name].filter(Boolean).join(' - ') };
    });
  } catch {
    return [];
  }
}

// ─────────────────────────────────────────────────── BOZUK İADE DENETİMİ (spec §10) — N+1 UYARISI

/**
 * Görünen SCRAP satırları için fotoğraf kanıtını çeker (denetim: `isSuspiciousScrap`).
 *
 * ⚠ N+1 — BİLEREK BIRAKILDI: fotoğraf sayısı OData'da YOKTUR. `PhotoAttachment` polimorfik bir
 * tablodur (EntityType="ReturnDetail", EntityId=ReturnDetail.Id) ve ReturnDetail'de ona giden bir
 * navigation property YOK → ne $expand ile ne de $filter ile ifade edilebilir. Sunulan tek kaynak
 * GET /api/returns/GetById'dir. Kalıcı çözüm BACKEND İŞİDİR (ReturnHeader'a türetilmiş bir
 * PhotoCount / HasPhotoEvidence alanı) ve bu refactor'ün kapsamı dışındadır.
 *
 * Hafifletme: istek YALNIZ GÖRÜNEN SAYFANIN bozuk satırları için atılır (sunucu sayfalaması
 * sayesinde ≤ pageSize) ve sonuç kalıcı önbelleğe alınır. Eskiden 120–500 satır için atılıyordu.
 */
export function useReturnAudit(rows) {
  const [auditMap, setAuditMap] = useState({});
  const requested = useRef(new Set());

  useEffect(() => {
    const targets = (rows || []).filter((r) => isScrap(r.returnSubType) && !requested.current.has(r.id));
    if (targets.length === 0) return undefined;
    targets.forEach((r) => requested.current.add(r.id));

    let cancelled = false;
    const headers = authHeaders();
    Promise.all(targets.map(async (r) => {
      try {
        const res = await fetch(`${API_BASE}/GetById?id=${r.id}`, { headers });
        if (!res.ok) return null;
        const d = unwrap(await res.json());
        return [r.id, auditSummaryFromItems(d?.items)];
      } catch {
        requested.current.delete(r.id); // geçici hata — sonraki turda yeniden denenebilsin
        return null;
      }
    })).then((pairs) => {
      if (cancelled) return;
      const next = {};
      pairs.filter(Boolean).forEach(([id, v]) => { next[id] = v; });
      if (Object.keys(next).length) setAuditMap((prev) => ({ ...prev, ...next }));
    });

    return () => { cancelled = true; };
  }, [rows]);

  /** Fotoğrafsız VEYA nedeni "diğer" olan bozuk iade → denetlenmesi gereken iade. */
  const suspicious = useCallback((r) => isSuspiciousScrap({
    subType: r.returnSubType,
    photoCount: auditMap[r.id]?.photoCount,
    // Kanıt henüz inmediyse hasar nedenleri $expand'dan zaten elimizde — yarısını hemen biliriz.
    damageReasons: auditMap[r.id] ? auditMap[r.id].damageReasons : r.damageReasons,
  }), [auditMap]);

  return { auditMap, suspicious };
}
